import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ApiException, api } from '../../api/client'
import type { CreateProjectBody, ProjectDetail } from '../../api/types.gen'
import { Banner, Button, Dialog, Field } from '../../ui'

/**
 * Crear un proyecto.
 *
 * Se monta al abrir y se desmonta al cerrar, igual que el de enlaces: así el
 * formulario empieza limpio sin reiniciarlo campo a campo desde un efecto.
 *
 * Un solo campo obligatorio, y es deliberado. El endpoint aceptaba `slug` y
 * `type` como requeridos, pero ninguno de los dos significa nada para quien
 * crea el proyecto: el slug no aparece en ninguna URL que él vea, y el tipo no
 * lo sabe todavía —acaba de crear la carpeta, aún no ha subido nada—.
 *
 * **`type` ya no es decorativo**: decide si la entrega se lee como retícula o
 * como lista, y con qué palabras. Pero se DEDUCE de lo que se sube, no se
 * pregunta (ver `ProjectType` en el backend): un arquitecto no sabe si lo suyo
 * es un `documents` o un `delivery`, sabe que son planos. Seguir sin
 * preguntarlo aquí es lo que hace que la fachada por vertical no le cueste
 * nada a nadie.
 */
export function CreateProjectDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [failure, setFailure] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () =>
      api.post<{ data: ProjectDetail }, CreateProjectBody>('/projects', { name: name.trim() }),
    onSuccess: (result) => {
      /*
       | Directo al proyecto recién creado, no de vuelta a la lista.
       |
       | Nadie crea un proyecto para mirarlo: lo crea para subir la sesión que
       | acaba de disparar. Devolver a la lista obliga a buscar entre treinta
       | tarjetas el que se acaba de hacer.
       */
      void navigate(`/projects/${result.data.id}`)
    },
    onError: (error) => {
      setFailure(
        error instanceof ApiException
          ? error.message
          : 'No se pudo crear el proyecto. Revisa tu conexión.',
      )
    },
  })

  const submit = (): void => {
    if (name.trim() === '') return

    setFailure(null)
    create.mutate()
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Nuevo proyecto"
      description="Un proyecto es una entrega: una boda, una sesión, un rodaje."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={name.trim() === ''}
            busy={create.isPending && 'Creando…'}
            onClick={submit}
          >
            Crear proyecto
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <Field
          label="¿Cómo se llama?"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Boda de Lucía y Diego"
          autoFocus
          maxLength={200}
          hint="Lo verá tu cliente en la cabecera de la galería, así que ponle el nombre con el que lo conocéis."
        />

        {/* Un submit oculto: sin él, Enter no envía un formulario de un solo
            campo en algunos navegadores, y aquí Enter es el gesto natural. */}
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />

        {failure !== null && (
          <Banner tone="danger" title="No se pudo crear" live="alert">
            {failure}
          </Banner>
        )}
      </form>
    </Dialog>
  )
}
